import React, { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowUp } from '@fortawesome/free-solid-svg-icons';
import CreateButton from '@frontend/Components/CreateButton/CreateButton';
import './FileDropzone.css';

export interface FileDropzoneProps {
  label?: string;
  helperText?: string;
  acceptText?: string;
  onFilesSelected?: (files: File[]) => void;
  className?: string;
}

/**
 * FileDropzone
 * Drag-and-drop upload area + \"Choose Files\" button (visual parity for redesign).
 * This is UI-first; `onFilesSelected` lets CreateMatters wire into a backend later.
 */
const FileDropzone: React.FC<FileDropzoneProps> = ({
  label = 'Add Documents',
  helperText = 'Drag and drop files here',
  acceptText = 'Supported formats: PDF, DOC, DOCX, XLS, XLSX (Max 10MB per file)',
  onFilesSelected,
  className = '',
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFiles = (fileList: FileList | null) => {
    if (!fileList) return;
    onFilesSelected?.(Array.from(fileList));
  };

  return (
    <div className={`file-dropzone ${className}`}>
      <div className="file-dropzone__label">{label}</div>

      <div
        className={`file-dropzone__box ${isDragging ? 'file-dropzone__box--drag' : ''}`}
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(false);
          handleFiles(e.dataTransfer.files);
        }}
      >
        <div className="file-dropzone__center">
          <div className="file-dropzone__icon">
            <FontAwesomeIcon icon={faCloudArrowUp} />
          </div>
          <div className="file-dropzone__text">
            <div className="file-dropzone__headline">{helperText}</div>
            <div className="file-dropzone__subline">or click to browse files</div>
          </div>

          <CreateButton
            onClick={() => inputRef.current?.click()}
            text="Choose Files"
            className="file-dropzone__button"
          />

          <div className="file-dropzone__accept">{acceptText}</div>
        </div>

        <input
          ref={inputRef}
          type="file"
          multiple
          className="file-dropzone__input"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>
    </div>
  );
};

export default FileDropzone;


